import {
	StackCompat as Stack,
	ListCompat as List,
	TextCompat as Text,
	useTheme,
	TooltipHostCompat as TooltipHost,
} from 'components/FluentUICompat';
import {LoadWrapper} from 'components/LoadWrapper';
import i18n from 'i18next';
import {formatDateTime, DateTimeFormat} from 'i18n/localeDateFormat';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {getListClassNames} from 'styles';
import {useGetVkoWorkbenchRegulatoryDocumentParagraphsQuery} from '../../hooks/workbenchItems.generated';
import {useVkoApproverWorkbenchStyles} from '../VkoApproverWorkbench/useVkoApproverWorkbenchStyles';
import i18next from 'i18next';
import {EmptyResults} from 'components/EntityList/EmptyResults';

type VkoParagraphsWorkbenchItemsProps = {
	isVkoApproverDashboard?: boolean;
	halfHeight?: boolean;
	title?: string;
};

export const VkoParagraphsWorkbenchItems: React.FC<
	VkoParagraphsWorkbenchItemsProps
> = ({halfHeight = false, isVkoApproverDashboard, title}) => {
	const {t} = useTranslation('features/dashboard', {
		keyPrefix: 'VkoParagraphsWorkbenchItems',
	});
	const {data, loading} = useGetVkoWorkbenchRegulatoryDocumentParagraphsQuery();

	const items = React.useMemo(
		() => /* data?.vkoWorkbenchRegulatoryDocumentParagraphs || */ [],
		[data],
	);

	const theme = useTheme();
	const classNames = getListClassNames(theme, halfHeight);
	const {classNames: vkoApproverClassNames} = useVkoApproverWorkbenchStyles();

	const onRenderCell = (item: any): React.ReactElement => {
		const renderValue = (val: string) => <b>{val}</b>;
		const renderReasonValue = (reason: string) => {
			if (reason.length > 80) {
				return (
					<TooltipHost content={reason}>
						{renderValue(reason.substring(0, 80) + '...')}
					</TooltipHost>
				);
			}
			return reason;
		};

		return (
			<div className={classNames.itemCell} data-is-focusable={true}>
				<div className={classNames.itemContent}>
					<Stack>
						<Text>
							{item?.regulatoryDocumentParagraph?.parent?.regulation?.name}
						</Text>
						<Text
							styles={{
								root: {fontSize: 18, fontWeight: 400, color: theme.palette.red},
							}}
						>
							{item?.regulatoryDocumentParagraph?.parent?.name}
						</Text>
						<Link
							to={`/regulations/${item?.regulatoryDocumentParagraph?.parent?.regulation?.id}/${item?.regulatoryDocumentParagraph?.parent?.id}/paragraphs`}
							className={classNames.link}
						>
							<div className={classNames.itemName}>
								{renderValue(item?.regulatoryDocumentParagraph?.enumeration)}
							</div>
						</Link>
						<Text>
							{t('VexCluster')} {renderValue(item?.vexClusterName)}
						</Text>
						<Text>
							{t('Keyword')} {renderValue(item?.keywordName)}
						</Text>
						{item?.rejectKeywordReason ? (
							<Text>
								{t('RejectKeywordReason')}{' '}
								{renderReasonValue(item.rejectKeywordReason.len)}
							</Text>
						) : (
							item?.rejectReason && (
								<Text>
									{t('RejectReason')} {renderReasonValue(item.rejectReason)}
								</Text>
							)
						)}
						{item?.rejectDate && (
							<Text>
								{t('RejectDate')}{' '}
								{renderValue(
									formatDateTime(
										new Date(item.rejectDate),
										i18next,
										DateTimeFormat.DateShort,
									),
								)}
							</Text>
						)}
						{item?.rejectAuthor && (
							<Text>
								{t('RejectAuthor')} {renderValue(item.rejectAuthor)}
							</Text>
						)}
					</Stack>
					<div className={classNames.itemSubtext}>
						{formatDateTime(
							new Date(item?.regulatoryDocumentParagraph?.createdAt),
							i18n,
							DateTimeFormat.DateMonthWeekday,
						)}
					</div>
				</div>
			</div>
		);
	};

	return (
		<LoadWrapper loading={loading}>
			<div
				className={
					isVkoApproverDashboard === true
						? vkoApproverClassNames.roundedDashboardActivityContainer
						: classNames.activityContainer
				}
			>
				<Stack horizontal horizontalAlign='space-between'>
					<Stack horizontal>
						<Stack.Item>
							<Text className={classNames.workbench}>{title}</Text>
						</Stack.Item>
						<Stack.Item>
							<Text className={classNames.itemCount}>
								{items.length > 30 ? `30+` : items.length}
							</Text>
						</Stack.Item>
					</Stack>
				</Stack>
				<div
					className={
						isVkoApproverDashboard === true
							? classNames.dashboardActivityContent
							: classNames.container
					}
					data-is-scrollable
				>
					{items.length === 0 && (
						<div style={{marginTop: 75}}>
							<EmptyResults />
						</div>
					)}
					<List items={items} onRenderCell={onRenderCell} />
				</div>
			</div>
		</LoadWrapper>
	);
};
